/**
 * Registry Client for Terraform Module Variables
 *
 * Handles fetching variable definitions from:
 * - Public Terraform Registry (registry.terraform.io)
 * - Private TFC Registry (app.terraform.io) - via backend proxy
 * - GitHub module sources - using user's GitHub token
 */

import {
  ModuleSourceInfo,
  isPublicRegistry,
  isPrivateRegistry,
  isGitHubSource,
  isLocalSource,
} from './moduleSourceParser';
import { parseHcl } from './hclParser';

/**
 * Terraform type definitions
 */
export type TerraformType =
  | 'string'
  | 'number'
  | 'bool'
  | 'list'
  | 'set'
  | 'map'
  | 'object'
  | 'tuple'
  | 'any'
  | 'dynamic';

/**
 * Variable schema from Terraform module
 */
export interface TerraformVariableSchema {
  name: string;
  type: TerraformType;
  description?: string;
  default?: unknown;
  required: boolean;
  sensitive: boolean;
  currentValue?: unknown;
  rawType?: string;
}

/**
 * Result from fetching module variables
 */
export interface FetchVariablesResult {
  variables: TerraformVariableSchema[];
  warnings: string[];
  source:
    | 'public-registry'
    | 'private-registry'
    | 'github'
    | 'backend-fallback';
}

/**
 * Module version info
 */
export interface ModuleVersionInfo {
  version: string;
  published_at?: string;
}

/**
 * Result from fetching module versions
 */
export interface FetchVersionsResult {
  versions: ModuleVersionInfo[];
  latestVersion?: string;
  source: 'public-registry' | 'private-registry' | 'github' | 'unknown';
  error?: string;
}

/**
 * Public Terraform Registry API response for module variables
 */
interface RegistryModuleResponse {
  root?: {
    inputs?: Array<{
      name: string;
      type?: string;
      description?: string;
      default?: unknown;
      required?: boolean;
    }>;
  };
  inputs?: Array<{
    name: string;
    type?: string;
    description?: string;
    default?: unknown;
    required?: boolean;
  }>;
}

/**
 * Compare two semantic version strings
 * Returns: negative if a < b, positive if a > b, 0 if equal
 */
function compareSemver(a: string, b: string): number {
  // Normalize versions (remove 'v' prefix if present)
  const normalizeVersion = (v: string) => v.replace(/^v/, '');
  const aNorm = normalizeVersion(a);
  const bNorm = normalizeVersion(b);

  // Split into parts: major.minor.patch[-prerelease][+build]
  const parseVersion = (v: string) => {
    const match = v.match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:-([^+]+))?(?:\+(.+))?$/);
    if (!match) return { major: 0, minor: 0, patch: 0, prerelease: '', build: '' };
    return {
      major: parseInt(match[1] || '0', 10),
      minor: parseInt(match[2] || '0', 10),
      patch: parseInt(match[3] || '0', 10),
      prerelease: match[4] || '',
      build: match[5] || '',
    };
  };

  const aParsed = parseVersion(aNorm);
  const bParsed = parseVersion(bNorm);

  // Compare major, minor, patch
  if (aParsed.major !== bParsed.major) return aParsed.major - bParsed.major;
  if (aParsed.minor !== bParsed.minor) return aParsed.minor - bParsed.minor;
  if (aParsed.patch !== bParsed.patch) return aParsed.patch - bParsed.patch;

  // Prerelease versions have lower precedence than release versions
  if (aParsed.prerelease && !bParsed.prerelease) return -1;
  if (!aParsed.prerelease && bParsed.prerelease) return 1;
  if (aParsed.prerelease && bParsed.prerelease) {
    return aParsed.prerelease.localeCompare(bParsed.prerelease);
  }

  return 0;
}

/**
 * Sort versions in descending order (newest first)
 */
function sortVersionsDescending(versions: ModuleVersionInfo[]): ModuleVersionInfo[] {
  return [...versions].sort((a, b) => compareSemver(b.version, a.version));
}

/**
 * Fetch options for the registry client
 */
export interface FetchVariablesOptions {
  /** User's GitHub token for private repos */
  githubToken?: string;
  /** Backstage FetchApi instance for authenticated backend calls */
  fetchApi?: { fetch: typeof fetch };
  /** Backend base URL for proxy calls */
  backendUrl?: string;
  /** Module version override */
  version?: string;
  /** Current values to apply to variables */
  currentValues?: Record<string, unknown>;
}

/**
 * Fetch the latest version from the public Terraform Registry
 */
async function fetchPublicRegistryLatestVersion(
  sourceInfo: ModuleSourceInfo,
): Promise<string | undefined> {
  if (!isPublicRegistry(sourceInfo)) {
    return undefined;
  }

  const modulePath = `${sourceInfo.namespace}/${sourceInfo.name}/${sourceInfo.provider}`;
  // The Terraform Registry API supports "latest" as a version to get the latest version info
  const url = `https://registry.terraform.io/v1/modules/${modulePath}`;

  try {
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      return undefined;
    }

    const data = (await response.json()) as {
      version?: string;
    };

    return data.version;
  } catch {
    return undefined;
  }
}

/**
 * Fetch available versions from the public Terraform Registry
 */
export async function fetchPublicRegistryVersions(
  sourceInfo: ModuleSourceInfo,
): Promise<{ versions: ModuleVersionInfo[]; latestVersion?: string }> {
  if (!isPublicRegistry(sourceInfo)) {
    throw new Error('Source is not a public registry module');
  }

  const modulePath = `${sourceInfo.namespace}/${sourceInfo.name}/${sourceInfo.provider}`;
  const url = `https://registry.terraform.io/v1/modules/${modulePath}/versions`;

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Module not found: ${modulePath}`);
    }
    throw new Error(
      `Registry API error: ${response.status} ${response.statusText}`,
    );
  }

  const data = (await response.json()) as {
    modules?: Array<{
      versions?: Array<{
        version: string;
        published_at?: string;
      }>;
    }>;
  };

  const versions = data.modules?.[0]?.versions || [];
  const versionList = versions.map(v => ({
    version: v.version,
    published_at: v.published_at,
  }));

  // Sort versions in descending order (newest first)
  const sortedVersions = sortVersionsDescending(versionList);

  // Fetch the actual latest version from the API
  const latestVersion = await fetchPublicRegistryLatestVersion(sourceInfo);

  return {
    versions: sortedVersions,
    latestVersion: latestVersion || (sortedVersions.length > 0 ? sortedVersions[0].version : undefined),
  };
}

/**
 * Fetch available versions from private TFC registry via backend proxy
 */
export async function fetchPrivateRegistryVersions(
  sourceInfo: ModuleSourceInfo,
  fetchApi: { fetch: typeof fetch },
  backendUrl: string,
): Promise<{ versions: ModuleVersionInfo[]; latestVersion?: string }> {
  if (!isPrivateRegistry(sourceInfo)) {
    throw new Error('Source is not a private registry module');
  }

  // Note: The backend versions endpoint matches the path but adds /versions at the end
  const modulePath = `${sourceInfo.namespace}/${sourceInfo.name}/${sourceInfo.provider}`;
  const url = `${backendUrl}/api/terraform-cloud/registry/modules/${modulePath}/versions?type=private&organization=${sourceInfo.organization}`;

  const response = await fetchApi.fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string; details?: string }).error ||
        (errorData as { details?: string }).details ||
        `Registry API error: ${response.status}`,
    );
  }

  const data = (await response.json()) as {
    versions?: Array<{
      version: string;
      published_at?: string;
    }>;
    latestVersion?: string;
  };

  const versionList = (data.versions || []).map(v => ({
    version: v.version,
    published_at: v.published_at,
  }));

  // Sort versions in descending order (newest first)
  const sortedVersions = sortVersionsDescending(versionList);

  return {
    versions: sortedVersions,
    latestVersion: data.latestVersion || (sortedVersions.length > 0 ? sortedVersions[0].version : undefined),
  };
}

/**
 * Fetch available versions from a GitHub module source (tags)
 */
export async function fetchGitHubVersions(
  sourceInfo: ModuleSourceInfo,
  githubToken?: string,
): Promise<ModuleVersionInfo[]> {
  if (!isGitHubSource(sourceInfo)) {
    throw new Error('Source is not a GitHub module');
  }

  const headers: Record<string, string> = {
    Accept: 'application/vnd.github.v3+json',
  };
  if (githubToken) {
    headers['Authorization'] = `Bearer ${githubToken}`;
  }

  const url = `https://api.github.com/repos/${sourceInfo.owner}/${sourceInfo.repo}/tags`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Repository not found: ${sourceInfo.owner}/${sourceInfo.repo}`);
    }
    throw new Error(`GitHub API error: ${response.status} ${response.statusText}`);
  }

  const tags = (await response.json()) as Array<{
    name: string;
    commit?: { sha?: string };
  }>;

  // Filter for semantic versions (v1.0.0, 1.0.0, etc.)
  const versionRegex = /^v?\d+\.\d+\.\d+/;
  return tags
    .filter(tag => versionRegex.test(tag.name))
    .map(tag => ({
      version: tag.name.replace(/^v/, ''), // Normalize to remove 'v' prefix
    }));
}

/**
 * Unified function to fetch versions based on source type
 */
export async function fetchModuleVersions(
  sourceInfo: ModuleSourceInfo,
  options: FetchVariablesOptions = {},
): Promise<FetchVersionsResult> {
  console.log('[registryClient] fetchModuleVersions called', {
    sourceInfo,
    hasGithubToken: !!options.githubToken,
    hasFetchApi: !!options.fetchApi,
    backendUrl: options.backendUrl,
  });

  const { githubToken, fetchApi, backendUrl } = options;

  try {
    if (isPublicRegistry(sourceInfo)) {
      console.log('[registryClient] Fetching versions from public registry...');
      const result = await fetchPublicRegistryVersions(sourceInfo);
      console.log('[registryClient] Public registry returned', result.versions.length, 'versions, latest:', result.latestVersion);
      return {
        versions: result.versions,
        latestVersion: result.latestVersion,
        source: 'public-registry',
      };
    } else if (isPrivateRegistry(sourceInfo) && fetchApi && backendUrl) {
      console.log('[registryClient] Fetching versions from private registry...');
      const result = await fetchPrivateRegistryVersions(sourceInfo, fetchApi, backendUrl);
      console.log('[registryClient] Private registry returned', result.versions.length, 'versions, latest:', result.latestVersion);
      return {
        versions: result.versions,
        latestVersion: result.latestVersion,
        source: 'private-registry',
      };
    } else if (isGitHubSource(sourceInfo)) {
      console.log('[registryClient] Fetching versions from GitHub...');
      const versions = await fetchGitHubVersions(sourceInfo, githubToken);
      // Sort GitHub versions by semver
      const sortedVersions = sortVersionsDescending(versions);
      const latestVersion = sortedVersions.length > 0 ? sortedVersions[0].version : undefined;
      console.log('[registryClient] GitHub returned', sortedVersions.length, 'versions, latest:', latestVersion);
      return {
        versions: sortedVersions,
        latestVersion,
        source: 'github',
      };
    } else if (isLocalSource(sourceInfo)) {
      console.log('[registryClient] Source is local path, cannot fetch versions');
      return {
        versions: [],
        source: 'unknown',
        error: 'Cannot fetch versions for local modules',
      };
    } else {
      console.log('[registryClient] Unsupported source type:', sourceInfo.type);
      return {
        versions: [],
        source: 'unknown',
        error: `Module source type "${sourceInfo.type}" is not supported for version discovery`,
      };
    }
  } catch (error) {
    console.error('[registryClient] Error fetching versions:', error);
    return {
      versions: [],
      source: 'unknown',
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Fetch variables from the public Terraform Registry
 */
export async function fetchPublicRegistryVariables(
  sourceInfo: ModuleSourceInfo,
  version?: string,
): Promise<TerraformVariableSchema[]> {
  if (!isPublicRegistry(sourceInfo)) {
    throw new Error('Source is not a public registry module');
  }

  const modulePath = `${sourceInfo.namespace}/${sourceInfo.name}/${sourceInfo.provider}`;
  const versionPath = version ? `/${version}` : '';
  const url = `https://registry.terraform.io/v1/modules/${modulePath}${versionPath}`;

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Module not found: ${modulePath}`);
    }
    throw new Error(
      `Registry API error: ${response.status} ${response.statusText}`,
    );
  }

  const data: RegistryModuleResponse = await response.json();
  const inputs = data.root?.inputs || data.inputs || [];

  return inputs.map(input => ({
    name: input.name,
    type: parseType(input.type),
    description: input.description,
    default: input.default,
    required: input.required ?? input.default === undefined,
    sensitive: false,
    rawType: input.type,
  }));
}

/**
 * Fetch variables from private TFC registry via backend proxy
 */
export async function fetchPrivateRegistryVariables(
  sourceInfo: ModuleSourceInfo,
  fetchApi: { fetch: typeof fetch },
  backendUrl: string,
  version?: string,
): Promise<TerraformVariableSchema[]> {
  if (!isPrivateRegistry(sourceInfo)) {
    throw new Error('Source is not a private registry module');
  }

  const modulePath = `${sourceInfo.namespace}/${sourceInfo.name}/${sourceInfo.provider}`;
  const versionPath = version ? `/${version}` : '';
  const url = `${backendUrl}/api/terraform-cloud/registry/modules/${modulePath}${versionPath}?type=private&organization=${sourceInfo.organization}`;

  const response = await fetchApi.fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string; details?: string }).error ||
        (errorData as { details?: string }).details ||
        `Registry API error: ${response.status}`,
    );
  }

  const data = (await response.json()) as {
    variables?: Array<{
      name: string;
      type?: string;
      description?: string;
      default?: unknown;
      required?: boolean;
      rawType?: string;
    }>;
  };

  return (data.variables || []).map(input => ({
    name: input.name,
    type: parseType(input.type),
    description: input.description,
    default: input.default,
    required: input.required ?? input.default === undefined,
    sensitive: false,
    rawType: input.rawType || input.type,
  }));
}

/**
 * Fetch variables from a GitHub module source
 */
export async function fetchGitHubVariables(
  sourceInfo: ModuleSourceInfo,
  githubToken?: string,
  version?: string,
): Promise<TerraformVariableSchema[]> {
  if (!isGitHubSource(sourceInfo)) {
    throw new Error('Source is not a GitHub module');
  }

  const ref = version || sourceInfo.ref || 'main';
  const basePath = sourceInfo.subdir ? `${sourceInfo.subdir}/` : '';
  const variablesPath = `${basePath}variables.tf`;

  const headers: Record<string, string> = {
    Accept: 'application/vnd.github.v3+json',
  };
  if (githubToken) {
    headers['Authorization'] = `Bearer ${githubToken}`;
  }

  const url = `https://api.github.com/repos/${sourceInfo.owner}/${sourceInfo.repo}/contents/${variablesPath}?ref=${ref}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(
        `variables.tf not found at ${variablesPath} in ${sourceInfo.owner}/${sourceInfo.repo}`,
      );
    }
    throw new Error(
      `GitHub API error: ${response.status} ${response.statusText}`,
    );
  }

  const fileData = (await response.json()) as {
    content?: string;
    encoding?: string;
  };

  if (!fileData.content) {
    throw new Error('No content in GitHub response');
  }

  // Decode base64 content
  const fileContent = atob(fileData.content.replace(/\n/g, ''));

  // Parse the HCL content using browser WASM
  const parsed = await parseHcl('variables.tf', fileContent);

  if (!parsed.variable) {
    return [];
  }

  // Convert parsed variables to schema format
  const variables: TerraformVariableSchema[] = [];

  for (const [name, varBlocks] of Object.entries(parsed.variable)) {
    const varBlock = Array.isArray(varBlocks) ? varBlocks[0] : varBlocks;

    if (varBlock && typeof varBlock === 'object') {
      const block = varBlock as {
        type?: string;
        description?: string;
        default?: unknown;
        sensitive?: boolean;
        nullable?: boolean;
      };

      // Determine the type
      let varType: TerraformType = 'any';
      let rawType: string | undefined;

      if (block.type) {
        rawType =
          typeof block.type === 'string'
            ? block.type
            : JSON.stringify(block.type);
        varType = parseType(rawType);
      }

      variables.push({
        name,
        type: varType,
        description: block.description,
        default: block.default,
        required: block.default === undefined && !block.nullable,
        sensitive: block.sensitive || false,
        rawType,
      });
    }
  }

  // Sort by required first, then alphabetically
  variables.sort((a, b) => {
    if (a.required !== b.required) return a.required ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return variables;
}

/**
 * Unified function to fetch variables based on source type
 */
export async function fetchModuleVariables(
  sourceInfo: ModuleSourceInfo,
  options: FetchVariablesOptions = {},
): Promise<FetchVariablesResult> {
  console.log('[registryClient] fetchModuleVariables called', {
    sourceInfo,
    hasGithubToken: !!options.githubToken,
    hasFetchApi: !!options.fetchApi,
    backendUrl: options.backendUrl,
    version: options.version,
  });

  const {
    githubToken,
    fetchApi,
    backendUrl,
    version,
    currentValues = {},
  } = options;
  const warnings: string[] = [];
  let variables: TerraformVariableSchema[] = [];
  let source: FetchVariablesResult['source'] = 'backend-fallback';

  try {
    if (isPublicRegistry(sourceInfo)) {
      console.log('[registryClient] Fetching from public registry...');
      variables = await fetchPublicRegistryVariables(sourceInfo, version);
      source = 'public-registry';
      console.log(
        '[registryClient] Public registry returned',
        variables.length,
        'variables',
      );
    } else if (isPrivateRegistry(sourceInfo) && fetchApi && backendUrl) {
      console.log('[registryClient] Fetching from private registry...');
      variables = await fetchPrivateRegistryVariables(
        sourceInfo,
        fetchApi,
        backendUrl,
        version,
      );
      source = 'private-registry';
      console.log(
        '[registryClient] Private registry returned',
        variables.length,
        'variables',
      );
    } else if (isGitHubSource(sourceInfo)) {
      console.log('[registryClient] Fetching from GitHub...');
      variables = await fetchGitHubVariables(sourceInfo, githubToken, version);
      source = 'github';
      console.log(
        '[registryClient] GitHub returned',
        variables.length,
        'variables',
      );
    } else if (isLocalSource(sourceInfo)) {
      console.log(
        '[registryClient] Source is local path, cannot fetch variables',
      );
      warnings.push(
        `Module source "${sourceInfo.path}" is a local path. Cannot fetch variable definitions for local modules.`,
      );
    } else if (isPrivateRegistry(sourceInfo)) {
      console.log(
        '[registryClient] Private registry but missing fetchApi or backendUrl',
      );
      warnings.push(
        'Private registry modules require backend proxy. Using fallback.',
      );
    } else {
      console.log('[registryClient] Unsupported source type:', sourceInfo.type);
      warnings.push(
        `Module source type "${sourceInfo.type}" is not supported for automatic variable discovery.`,
      );
    }
  } catch (error) {
    console.error('[registryClient] Error fetching variables:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    warnings.push(`Failed to fetch variables: ${errorMessage}`);
  }

  // Apply current values to variables
  if (Object.keys(currentValues).length > 0) {
    variables = variables.map(v => ({
      ...v,
      currentValue: currentValues[v.name],
    }));
  }

  return {
    variables,
    warnings,
    source,
  };
}

/**
 * Parse Terraform type string into our type enum
 */
function parseType(typeStr?: string): TerraformType {
  if (!typeStr) return 'any';

  const normalized = typeStr
    .replace(/^\$\{/, '')
    .replace(/\}$/, '')
    .trim()
    .toLowerCase();

  if (['string', 'number', 'bool'].includes(normalized)) {
    return normalized as TerraformType;
  }
  if (normalized.startsWith('list')) return 'list';
  if (normalized.startsWith('set')) return 'set';
  if (normalized.startsWith('map')) return 'map';
  if (normalized.startsWith('object')) return 'object';
  if (normalized.startsWith('tuple')) return 'tuple';
  if (normalized === 'any' || normalized === 'dynamic') {
    return normalized as TerraformType;
  }

  return 'any';
}
